import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import DropZone from 'components/DropZone';
import ProductFormFields from './ProductFormFields';

const validationSchema = Yup.object({
  nameBG: Yup.string().required('Задължително поле'),
  nameRU: Yup.string().required('Задължително поле'),
  nameEN: Yup.string().required('Задължително поле'),
  code: Yup.string().required('Задължително поле'),
  status: Yup.string().required('Задължително поле'),
  category: Yup.number().required('Задължително поле'),
  subCategory: Yup.number().required('Задължително поле'),
  productTypeAttribute: Yup.number().required('Задължително поле'),
  materialAttribute: Yup.number().required('Задължително поле'),
  descriptionBG: Yup.string().required('Задължително поле'),
  descriptionRU: Yup.string().required('Задължително поле'),
  descriptionEN: Yup.string().required('Задължително поле'),
  summaryBG: Yup.string().required('Задължително поле'),
  summaryRU: Yup.string().required('Задължително поле'),
  summaryEN: Yup.string().required('Задължително поле')
});

const imagePosition = (image) => {
  const value = image?.Product_Image?.position;
  if (value === '' || value === null || typeof value === 'undefined')
    return Number.POSITIVE_INFINITY;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : Number.POSITIVE_INFINITY;
};

const sortImagesByPosition = (images = []) =>
  [...images].sort((first, second) => imagePosition(first) - imagePosition(second));

export default function ProductDetails() {
  const params = useParams();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [imagesData, setImagesData] = useState([]);
  const [initialImages, setInitialImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [imageFeedback, setImageFeedback] = useState(null);
  const [uploadFeedback, setUploadFeedback] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setIsLoading(true);
      try {
        const [
          productResponse,
          categoryResponse,
          subcategoryResponse,
          typeResponse,
          materialResponse
        ] = await Promise.all([
          axios.get('/singleProduct/', { params }),
          axios.get('/category'),
          axios.get('/subcategory'),
          axios.get('/productType'),
          axios.get('/material')
        ]);
        const loadedProduct = productResponse.data[0];
        if (!loadedProduct) throw new Error('Product not found');
        loadedProduct.status = loadedProduct.status || 'published';
        setProduct(loadedProduct);
        setInitialImages(sortImagesByPosition(loadedProduct.images));
        setCategories(categoryResponse.data);
        setSubcategories(subcategoryResponse.data);
        setProductTypes(typeResponse.data);
        setMaterials(materialResponse.data);
      } catch (error) {
        setLoadError('Продуктът не можа да бъде зареден.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPage();
  }, [params.id]);

  const initialValues = {
    productId: params.id,
    status: product?.status || 'published',
    position: product?.position ?? 1000000000,
    position_landing: product?.position_landing ?? 1000000000,
    nameBG: product?.name_bg || '',
    nameRU: product?.name_ru || '',
    nameEN: product?.name_en || '',
    code: product?.code || '',
    category: product?.categoryId || '',
    subCategory: product?.subCategoryId || '',
    productTypeAttribute: product?.productTypeAttributeId || '',
    materialAttribute: product?.materialAttributeId || '',
    summaryBG: product?.summary_bg || '',
    summaryRU: product?.summary_ru || '',
    summaryEN: product?.summary_en || '',
    descriptionBG: product?.description_bg || '',
    descriptionRU: product?.description_ru || '',
    descriptionEN: product?.description_en || ''
  };

  const handleSubmit = async (values) => {
    setSaveFeedback({ severity: 'info', message: 'Запазване на промените…' });
    try {
      await axios.post('/updateProduct', { values });
      setProduct((current) => ({
        ...current,
        code: values.code,
        name_bg: values.nameBG,
        name_en: values.nameEN,
        name_ru: values.nameRU,
        status: values.status
      }));
      setSaveFeedback({ severity: 'success', message: 'Продуктът беше обновен успешно.' });
    } catch (error) {
      setSaveFeedback({
        severity: 'error',
        message: 'Продуктът не можа да бъде обновен. Моля опитайте отново.'
      });
    }
  };

  const handleAddImages = async () => {
    if (!imagesData.length) return;
    setUploadFeedback({ severity: 'info', message: 'Качване на новите снимки…' });
    try {
      for (const image of imagesData) {
        await axios.post('/createProductImages', {
          imagesData: [image],
          targetId: params.id
        });
      }
      setUploadFeedback({ severity: 'success', message: 'Новите снимки бяха добавени успешно.' });
      setImagesData([]);
      const { data } = await axios.get('/singleProduct/', { params });
      if (data[0]) {
        setProduct(data[0]);
        setInitialImages(sortImagesByPosition(data[0].images));
      }
    } catch (error) {
      setUploadFeedback({ severity: 'error', message: 'Някои снимки не можаха да бъдат качени.' });
    }
  };

  const handleDelete = (image) => {
    setInitialImages((current) => current.filter((item) => item !== image));
  };

  const handleUpdateImages = async () => {
    setImageFeedback({ severity: 'info', message: 'Запазване на снимките…' });
    try {
      await axios.post('/updateProductImages', {
        imagesData: initialImages,
        initialImages: product.images,
        targetId: params.id
      });
      const sortedImages = sortImagesByPosition(initialImages);
      setInitialImages(sortedImages);
      setProduct((current) => ({ ...current, images: sortedImages }));
      setImageFeedback({ severity: 'success', message: 'Снимките бяха обновени успешно.' });
    } catch (error) {
      setImageFeedback({ severity: 'error', message: 'Снимките не можаха да бъдат обновени.' });
    }
  };

  const handlePositionChange = (event, index) => {
    const { value } = event.target;
    setInitialImages((current) =>
      current.map((image, imageIndex) =>
        imageIndex === index
          ? { ...image, Product_Image: { ...image.Product_Image, position: value } }
          : image
      )
    );
  };

  const handlePositionBlur = () => {
    setInitialImages((current) => sortImagesByPosition(current));
  };

  const headerActions = (
    <Stack direction="row" gap={1}>
      <Button component={Link} to="/products" startIcon={<ArrowBackRoundedIcon />}>
        Назад
      </Button>
      {product && (
        <Button
          variant="outlined"
          component="a"
          href={`https://gotobed.eu/produkt/${product.slug_bg}/${product.id}`}
          target="_blank"
          rel="noreferrer"
          endIcon={<OpenInNewRoundedIcon />}
        >
          Виж в сайта
        </Button>
      )}
    </Stack>
  );

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Редакция на продукт"
        subtitle={product ? `${product.name_bg} · ${product.code} · #${product.id}` : 'Зареждане…'}
        icon={EditRoundedIcon}
        actions={headerActions}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}
      {loadError && <Alert severity="error">{loadError}</Alert>}

      {product && (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent
                sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}
              >
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Form>
                      <ProductFormFields
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        categories={categories}
                        subcategories={subcategories}
                        productTypes={productTypes}
                        materials={materials}
                        positionField="position_landing"
                        positionLabel="Позиция на началната страница"
                      />
                      {saveFeedback && (
                        <Alert severity={saveFeedback.severity} sx={{ mt: 3 }}>
                          {saveFeedback.message}
                        </Alert>
                      )}
                      {Object.keys(errors).length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Проверете задължителните полета във всички езикови табове.
                        </Alert>
                      )}
                      <Stack direction="row" justifyContent="flex-end" mt={3}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<SaveRoundedIcon />}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Запазване…' : 'Запази промените'}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3} sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
              <Card>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <ImageRoundedIcon color="primary" />
                      <Typography variant="h6">Текущи снимки</Typography>
                    </Stack>
                    <Chip label={initialImages.length} size="small" />
                  </Stack>
                  <Stack spacing={1.5}>
                    {initialImages.map((image, index) => (
                      <PaperImage
                        key={image.id || image.url}
                        image={image}
                        index={index}
                        onPositionChange={handlePositionChange}
                        onPositionBlur={handlePositionBlur}
                        onDelete={handleDelete}
                      />
                    ))}
                    {!initialImages.length && (
                      <Typography variant="body2" color="text.secondary">
                        Няма добавени снимки.
                      </Typography>
                    )}
                  </Stack>
                  {imageFeedback && (
                    <Alert severity={imageFeedback.severity} sx={{ mt: 2 }}>
                      {imageFeedback.message}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={handleUpdateImages}
                    disabled={!initialImages.length}
                  >
                    Запази снимките
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                  <Typography variant="h6" mb={0.5}>
                    Добавяне на снимки
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Изберете до 15 изображения и задайте позицията им.
                  </Typography>
                  <DropZone imagesData={imagesData} setImagesData={setImagesData} />
                  {uploadFeedback && (
                    <Alert severity={uploadFeedback.severity} sx={{ mt: 2 }}>
                      {uploadFeedback.message}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleAddImages}
                    disabled={!imagesData.length}
                  >
                    Качи снимките
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}
    </VendorDashboardLayout>
  );
}

function PaperImage({ image, index, onPositionChange, onPositionBlur, onDelete }) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: '#fafbfc',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: '58px 1fr auto',
        p: 1
      }}
    >
      <Box
        component="img"
        src={image.url}
        alt={`Снимка ${index + 1}`}
        sx={{ borderRadius: 2, height: 58, objectFit: 'cover', width: 58 }}
      />
      <TextField
        name={`imagePosition-${index}`}
        label="Позиция"
        type="number"
        value={image.Product_Image?.position ?? ''}
        onChange={(event) => onPositionChange(event, index)}
        onBlur={onPositionBlur}
      />
      <Tooltip title="Премахни">
        <IconButton color="error" onClick={() => onDelete(image)} aria-label="Премахни снимката">
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
